import React from 'react';
import PropTypes from 'prop-types';

import { emptyArray } from 'ui-components/default-props/empty';
import { messages } from '../../messages';
import { Providers } from '../../../enums/providers';
import { linkIDs } from '../../../services/link-service/links';
import DataDrivenLogo from '../../../components/data-driven-logo';
import getDisplayInformation from '../../../components/data-driven-logo/get-display-information';
import GetCarrierDisplayName from '../../../components/data-driven-logo/get-carrier-display-name';
import HelpArticleLink from '../../../components/help-article-link';
import styles from './carrier-update-modal.module.less';
import GetInsuranceProviderDisplayName from '../../../components/data-driven-logo/get-insurance-provider-display-name';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';

const preferredProviderOrder = [
  Providers.Stamps.labelProviderId,
  Providers.UPSWallet.labelProviderId,
  Providers.FedExWalleted.labelProviderId,
];

export const getPrioritizedProviderOrder = (providers) => {
  const providersToUse = providers ?? [];
  return providersToUse.toSorted((a, b) => {
    const aRank = preferredProviderOrder.indexOf(a) === -1 ? 100 : preferredProviderOrder.indexOf(a);
    const bRank = preferredProviderOrder.indexOf(b) === -1 ? 100 : preferredProviderOrder.indexOf(b);
    return aRank - bRank;
  });
};

export const ResourceDisplayInfo = ({
  labelProviderIdsToRegister = emptyArray,
  newResourceUpdates,
  newInsuranceProviderIds = emptyArray,
}) => {
  const providerIdToKbArticle = {
    [Providers.Canpar.labelProviderId]: linkIDs.CanparOneBalanceKb,
    [Providers.CanadaPost1B.labelProviderId]: linkIDs.CanadaPostOneBalanceKb,
  };

  const alternateLabelProviderIdMap = {
    [Providers.Canpar1B.labelProviderId]: Providers.Canpar.labelProviderId,
  };

  const getSanitizedLabelProviderId = (labelProviderId) => {
    const mappedId = alternateLabelProviderIdMap[labelProviderId];
    const correctId = mappedId !== undefined ? mappedId : labelProviderId;

    return Number.parseInt(correctId, 10);
  };
  return (
    <FlexColumn className={styles['right-side']}>
      <FlexColumn className={styles['right-header']}>
        <Text textType={TextType.H2} value={messages.updateModalRightHeader} />
        <Text textType={TextType.Paragraph} value={messages.updateModalRightInfo} />
      </FlexColumn>
      {newResourceUpdates?.map((resource) => {
        const providerId = getSanitizedLabelProviderId(resource.resourceId);
        const articleIdForProvider = providerIdToKbArticle[providerId];
        const carrierDisplayInfo = getDisplayInformation({ providerId });
        const carrierMessageDescriptor = GetCarrierDisplayName(carrierDisplayInfo?.carrierId);
        return (
          <FlexRow key={`new-${resource.resourceId}-${resource.name}`} className={styles['provider-row']}>
            <div className={styles['label-provider-logo']}>
              <DataDrivenLogo logoType='icon' providerId={Number.parseInt(resource.resourceId, 10)} />
            </div>
            {articleIdForProvider ? (
              <HelpArticleLink articleId={articleIdForProvider}>
                <Text
                  formattedMessageValues={carrierMessageDescriptor.values}
                  textType={TextType.Paragraph}
                  value={carrierMessageDescriptor}
                />
              </HelpArticleLink>
            ) : (
              <Text
                formattedMessageValues={carrierMessageDescriptor.values}
                textType={TextType.Paragraph}
                value={carrierMessageDescriptor}
              />
            )}
          </FlexRow>
        );
      })}
      {getPrioritizedProviderOrder(labelProviderIdsToRegister)?.map((labelProviderId) => {
        const providerId = getSanitizedLabelProviderId(labelProviderId);
        const articleIdForProvider = providerIdToKbArticle[providerId];
        const carrierDisplayInfo = getDisplayInformation({ providerId });
        const sanitizeBranding = true;
        const carrierMessageDescriptor = GetCarrierDisplayName(carrierDisplayInfo?.carrierId, sanitizeBranding);
        return (
          <FlexRow key={labelProviderId} className={styles['provider-row']}>
            <div className={styles['label-provider-logo']}>
              <DataDrivenLogo logoType='icon' providerId={Number.parseInt(labelProviderId, 10)} />
            </div>
            {articleIdForProvider ? (
              <HelpArticleLink articleId={articleIdForProvider}>
                <Text
                  formattedMessageValues={carrierMessageDescriptor.values}
                  textType={TextType.Paragraph}
                  value={carrierMessageDescriptor}
                />
              </HelpArticleLink>
            ) : (
              <Text
                formattedMessageValues={carrierMessageDescriptor.values}
                textType={TextType.Paragraph}
                value={carrierMessageDescriptor}
              />
            )}
          </FlexRow>
        );
      })}
      {newInsuranceProviderIds.map((insuranceProviderId) => {
        const insuranceDisplayInfo = getDisplayInformation({
          insuranceProviderId,
        });
        const insuranceMessageDescriptor = GetInsuranceProviderDisplayName(insuranceDisplayInfo?.insuranceProviderId);

        return (
          <div key={insuranceProviderId} className={styles['provider-row']}>
            <div className={styles['label-provider-logo']}>
              <DataDrivenLogo insuranceProviderId={insuranceProviderId} logoType='icon' showIcon />
            </div>
            <Text
              formattedMessageValues={insuranceMessageDescriptor?.values}
              textType={TextType.Paragraph}
              value={insuranceMessageDescriptor}
            />
          </div>
        );
      })}
      <Text
        formattedMessageValues={{
          kb: (chunks) => <HelpArticleLink articleId={linkIDs.WalletRegistrationHelpLinks}>{chunks}</HelpArticleLink>,
        }}
        textType={TextType.Paragraph}
        value={messages.updateModalRightMoreInfo}
      />
    </FlexColumn>
  );
};

ResourceDisplayInfo.propTypes = {
  labelProviderIdsToRegister: PropTypes.array,
  newResourceUpdates: PropTypes.array.isRequired,
  newInsuranceProviderIds: PropTypes.array,
};
